import React, { useCallback, useState, useEffect, useRef } from 'react';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { ExpensesTable } from './ExpensesTable';
import { ExpenseSidebar } from './ExpenseSidebar/ExpenseSidebar';
import {
  useDeleteExpenseMutation,
  useExpensesQuery,
  useDeleteExpenseGroupMutation,
  useUpdateExpenseGroupStatusMutation,
  ExpenseStatus,
  ExpenseSortFieldEnum,
  AscDescEnum,
} from '../../__generated__/graphql';
import { Expense } from './types';
import { mapExpense } from './mappings';
import ServerError from '../../../../components/ServerError';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { routes, routes as mainRoutes } from '../../routes';
import {
  ExpenseDeleteConfirmation,
  ExpenseGroupDeleteConfirmation,
} from './DeleteConfirmations';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { ExpenseSaveModal } from './ExpenseSaveModal';
import { ExpenseGroupCreateModal } from './ExpenseGroupCreateModal';
import { Maybe } from '../../../../utils/maybe';
import { useHistory, useParams } from 'react-router-dom';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { useSelectedItemWithComments } from '../../components/Comment';
import { Item } from './useExpenseTableRenderSettings';
import { ExpenseGroupWithRelations } from './ExpensesTable/ExpensesTable';
import { buildExpenseQueryParams } from './queryParamsHelpers';
import { createShowCommentQueryParams } from '../../components/Comment';
import {
  useObjectExtraFields,
  ExtraFieldsSupportedObject,
} from '../../components/ExtraFields';
import { validateExpense } from './expenseValidation';

const MyExpenses: React.FC = () => {
  const history = useHistory();
  const { id: routeExpenseId } = useParams<{ id: string }>();
  const { defaultSelectedItem: selectedItemWithComments } =
    useSelectedItemWithComments();

  const { data, loading, error, refetch } = useExpensesQuery({
    variables: {
      sort: [
        { field: ExpenseSortFieldEnum.CreatedDate, order: AscDescEnum.Desc },
      ],
    },
  });

  const { getFormFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.Expense,
  );
  const [deleteExpense, { loading: deleteInProgress }] =
    useDeleteExpenseMutation();
  const [deleteExpenseGroup, { loading: deleteExpenseGroupInProgress }] =
    useDeleteExpenseGroupMutation();
  const [updateExpenseGroupStatus] = useUpdateExpenseGroupStatusMutation();
  const errorHandler = useMutationErrorHandler();

  const { t } = useTranslation();

  useFlairBreadcrumbHook([
    { label: t('navigation.menuItems.profile.title') },
    { label: t('expenses.title') },
  ]);

  const { addSuccess, addError } = useToasts();

  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [deleteExpenseGroupId, setDeleteExpenseGroupId] = useState<
    string | null
  >(null);
  const [deleteExpenseGroupInfo, setDeleteExpenseGroupInfo] = useState<{
    id: string;
    name: string;
    childCount: number;
  } | null>(null);
  const [submittingExpenseGroupId, setSubmittingExpenseGroupId] = useState<
    string | null
  >(null);
  const [invalidExpenseIds, setInvalidExpenseIds] = useState<Set<string>>(
    new Set(),
  );
  const [saveSideBarVisible, setSaveSideBarVisible] = useState<boolean>(false);
  const [prefilledExpenseGroupId, setPrefilledExpenseGroupId] = useState<
    string | undefined
  >(undefined);
  const [createExpenseGroupModalVisible, setCreateExpenseGroupModalVisible] =
    useState<boolean>(false);
  const [expenseFormRefreshTrigger, setExpenseFormRefreshTrigger] =
    useState<number>(0);
  const [editExpenseModalVisible, setEditExpenseModalVisible] =
    useState<boolean>(false);
  const [editExpenseId, setEditExpenseId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const expandedParam = urlParams.get('expanded');
    return expandedParam
      ? new Set(expandedParam.split(',').filter(Boolean))
      : new Set();
  });

  const handleExpandedGroupsChange = useCallback(
    (groups: Set<string>) => {
      setExpandedGroups(groups);

      const currentQueryParams = Object.fromEntries(
        new URLSearchParams(window.location.search),
      );

      const allQueryParams = buildExpenseQueryParams({
        item: null,
        expandedGroups: groups,
        currentQueryParams,
      });

      history.replace(
        routes.myExpenses.route.withQueryParams(allQueryParams).create({}),
      );
    },
    [history],
  );

  useEffect(() => {
    if (!loading && data && window.location.hash) {
      const targetId = window.location.hash.substring(1);

      const scrollToElement = () => {
        const element = document.getElementById(targetId);
        if (element) {
          const elementTop =
            element.getBoundingClientRect().top + window.pageYOffset;
          const offsetTop = elementTop - 4 * 16;

          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
          return true;
        }
        return false;
      };

      const attemptScroll = () => {
        if (!scrollToElement()) {
          setTimeout(attemptScroll, 100);
        }
      };

      setTimeout(attemptScroll, 300);
    }
  }, [loading, data]);

  const previousExpensesDataRef = useRef<string>('');

  // Re-validate previously invalid expenses when expense data changes.
  useEffect(() => {
    if (loading || !data?.me.myExpenses) {
      return;
    }

    const expenses = data.me.myExpenses;
    const createExpensesFingerprint = (expenseList: typeof expenses) =>
      JSON.stringify(
        expenseList.map((exp) => ({
          Id: exp.Id,
          Description: exp.flair__Description__c,
          MerchantId: exp.merchant?.Id,
          CategoryId: exp.expenseCategory?.Id,
          ExtraFields: exp.extraFields?.map((ef) => ({
            fieldApiName: ef.fieldApiName,
            jsonValue: ef.jsonValue,
          })),
        })),
      );

    const expensesKey = createExpensesFingerprint(expenses);
    if (previousExpensesDataRef.current === expensesKey) {
      return;
    }
    previousExpensesDataRef.current = expensesKey;
    setInvalidExpenseIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      const next = new Set(prev);
      let hasChanges = false;

      prev.forEach((invalidId) => {
        const expense = expenses.find((exp) => exp.Id === invalidId);
        if (expense && validateExpense(expense, { getFormFields })) {
          next.delete(invalidId);
          hasChanges = true;
        } else if (!expense) {
          next.delete(invalidId);
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [loading, data?.me.myExpenses, getFormFields]);

  const expenseDelete = useCallback(async () => {
    if (deleteItemId === null) {
      return;
    }

    setInvalidExpenseIds((prev) => {
      const next = new Set(prev);
      next.delete(deleteItemId);
      return next;
    });

    await deleteExpense({
      variables: {
        id: deleteItemId,
      },
      update: (cache, { data }) => {
        if (!data?.expenses?.deleteExpense?.query?.me) return;

        const newExpenses = data.expenses.deleteExpense.query.me.myExpenses;

        cache.modify({
          fields: {
            me: (existingMe, { readField }) => {
              const meId = readField('Id', existingMe);
              const meCacheId = cache.identify({ __typename: 'Me', Id: meId });

              if (meCacheId) {
                cache.modify({
                  id: meCacheId,
                  fields: {
                    myExpenses: () => newExpenses,
                  },
                });
              }

              return existingMe;
            },
          },
        });
      },
    })
      .then((r) => {
        if (r.data?.expenses.deleteExpense.error) {
          addError(r.data?.expenses.deleteExpense.error);
          return;
        }
        addSuccess(t('expenses.expenseDeletedToast'));
      })
      .catch(errorHandler)
      .finally(() => {
        setDeleteItemId(null);
      });
  }, [addError, addSuccess, deleteExpense, deleteItemId, errorHandler, t]);

  const handleCreateBtnClick = useCallback(() => {
    setSaveSideBarVisible(true);
  }, []);

  const handleSaveSidebarClose = useCallback(
    (groupId?: string) => {
      setSaveSideBarVisible(false);
      setPrefilledExpenseGroupId(undefined);
      refetch();

      if (groupId) {
        setExpandedGroups((prev) => {
          const newExpandedGroups = new Set([...prev, groupId]);

          const currentQueryParams = Object.fromEntries(
            new URLSearchParams(window.location.search),
          );
          const allQueryParams = buildExpenseQueryParams({
            item: null,
            expandedGroups: newExpandedGroups,
            currentQueryParams,
          });

          const newUrl = routes.myExpenses.route
            .withQueryParams(allQueryParams)
            .create({});
          history.replace(`${newUrl}#${groupId}`);

          return newExpandedGroups;
        });
      }
    },
    [refetch, history],
  );

  const handleAddExpense = useCallback((expenseGroupId: string) => {
    setPrefilledExpenseGroupId(expenseGroupId);
    setSaveSideBarVisible(true);
  }, []);

  const handleCreateExpenseGroupBtnClick = useCallback(() => {
    setCreateExpenseGroupModalVisible(true);
  }, []);

  const handleCreateExpenseGroupModalClose = useCallback(() => {
    setCreateExpenseGroupModalVisible(false);
  }, []);

  const handleCreateExpenseGroupModalSave = useCallback(
    (createdExpenseGroupId?: string) => {
      setCreateExpenseGroupModalVisible(false);
      setExpenseFormRefreshTrigger((prev) => prev + 1);

      if (createdExpenseGroupId) {
        setExpandedGroups((prev) => {
          const newExpandedGroups = new Set([...prev, createdExpenseGroupId]);

          const currentQueryParams = Object.fromEntries(
            new URLSearchParams(window.location.search),
          );
          const allQueryParams = buildExpenseQueryParams({
            item: null,
            expandedGroups: newExpandedGroups,
            currentQueryParams,
          });

          const newUrl = routes.myExpenses.route
            .withQueryParams(allQueryParams)
            .create({});
          history.replace(`${newUrl}#${createdExpenseGroupId}`);

          return newExpandedGroups;
        });
      }
    },
    [history],
  );

  const handleEditExpenseModalClose = useCallback(() => {
    setEditExpenseModalVisible(false);
    setEditExpenseId(null);
  }, []);

  const handleEditExpenseModalSave = useCallback(() => {
    if (editExpenseId) {
      setInvalidExpenseIds((prev) => {
        const next = new Set(prev);
        next.delete(editExpenseId);
        return next;
      });
    }
    setEditExpenseModalVisible(false);
    setEditExpenseId(null);
    setExpenseFormRefreshTrigger((prev) => prev + 1);
  }, [editExpenseId]);

  const handleSubmitExpenseGroup = useCallback(
    async (expenseGroupId: string) => {
      try {
        if (data?.me.myExpenses) {
          const childExpenses = data.me.myExpenses.filter(
            (exp) => exp.flair__Expense_Group__c === expenseGroupId,
          );
          const incompleteExpenses = childExpenses.filter(
            (exp) => !validateExpense(exp, { getFormFields }),
          );
          if (incompleteExpenses.length > 0) {
            const incompleteIds = new Set(
              incompleteExpenses.map((exp) => exp.Id),
            );
            setInvalidExpenseIds(incompleteIds);
            setExpandedGroups((prev) => new Set([...prev, expenseGroupId]));
            addError(t('expenses.expenseGroupSubmissionIncompleteData'));
            return;
          }
        }

        setSubmittingExpenseGroupId(expenseGroupId);
        await updateExpenseGroupStatus({
          variables: {
            input: {
              id: expenseGroupId,
              status: ExpenseStatus.Pending,
              declineReason: null,
              reimbursed: null,
            },
          },
        });
        addSuccess(t('expenses.expenseGroupSubmittedToast'));
        setInvalidExpenseIds(new Set());
      } catch (error) {
        errorHandler(error);
      } finally {
        setSubmittingExpenseGroupId(null);
      }
    },
    [
      addSuccess,
      addError,
      t,
      errorHandler,
      updateExpenseGroupStatus,
      data,
      getFormFields,
    ],
  );

  const expenseGroupDelete = useCallback(
    async (idToDelete?: string) => {
      const targetId =
        idToDelete || deleteExpenseGroupId || deleteExpenseGroupInfo?.id;
      if (!targetId) {
        return;
      }
      await deleteExpenseGroup({
        variables: {
          id: targetId,
        },
        update: (cache, { data }) => {
          if (!data?.expenseGroups?.deleteExpenseGroup?.query?.me) return;
          const newExpenseGroups =
            data.expenseGroups.deleteExpenseGroup.query.me.myExpenseGroups;
          const newExpenses =
            data.expenseGroups.deleteExpenseGroup.query.me.myExpenses;

          cache.modify({
            fields: {
              me: (existingMe, { readField }) => {
                const meId = readField('Id', existingMe);
                const meCacheId = cache.identify({
                  __typename: 'Me',
                  Id: meId,
                });

                if (meCacheId) {
                  cache.modify({
                    id: meCacheId,
                    fields: {
                      myExpenseGroups: () => newExpenseGroups,
                      myExpenses: () => newExpenses,
                    },
                  });
                }
                return existingMe;
              },
            },
          });
        },
      })
        .then((r: any) => {
          if (r.data?.expenseGroups.deleteExpenseGroup.error) {
            addError(r.data?.expenseGroups.deleteExpenseGroup.error);
            return;
          }
          addSuccess(t('expenses.expenseGroupDeletedToast'));
        })
        .catch(errorHandler)
        .finally(() => {
          setDeleteExpenseGroupId(null);
          setDeleteExpenseGroupInfo(null);
        });
    },
    [
      addError,
      addSuccess,
      deleteExpenseGroup,
      deleteExpenseGroupId,
      deleteExpenseGroupInfo?.id,
      errorHandler,
      t,
    ],
  );

  const handleDeleteExpenseGroup = useCallback(
    (
      expenseGroupId: string,
      expenseGroupName: string,
      childExpenseCount: number,
    ) => {
      if (childExpenseCount === 0) {
        setDeleteExpenseGroupId(expenseGroupId);
        expenseGroupDelete(expenseGroupId);
      } else {
        setDeleteExpenseGroupInfo({
          id: expenseGroupId,
          name: expenseGroupName,
          childCount: childExpenseCount,
        });
      }
    },
    [expenseGroupDelete],
  );

  const handleDeleteExpenseGroupClose = () => {
    setDeleteExpenseGroupId(null);
    setDeleteExpenseGroupInfo(null);
  };

  if (error) {
    return <ServerError />;
  }

  const handleDelete = (expenseId: string) => {
    setDeleteItemId(expenseId);
  };

  const handleDeleteClose = () => {
    setDeleteItemId(null);
  };

  let allExpenses: Expense[] = data?.me.myExpenses.map(mapExpense) || [];
  let allExpenseGroups: ExpenseGroupWithRelations[] = [
    ...(data?.me.myExpenseGroups ?? []),
  ];

  const handleOnItemClick = (item: Maybe<Item>) => {
    const currentQueryParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    const allQueryParams = buildExpenseQueryParams({
      item,
      expandedGroups,
      currentQueryParams,
    });

    if (item?.id) {
      const expense = allExpenses.find((exp) => exp.id === item.id);
      if (expense?.status === ExpenseStatus.Draft) {
        setEditExpenseId(item.id);
        setEditExpenseModalVisible(true);
        return;
      }

      const currentQueryParams = Object.fromEntries(
        new URLSearchParams(window.location.search),
      );

      const commentParams = item.showComments
        ? createShowCommentQueryParams({ recordId: item.id })
        : {};

      const itemsParams: Record<string, string> = item.showItems
        ? { showItems: 'true' }
        : {};

      const expandedParam: Record<string, string> =
        expandedGroups.size > 0
          ? { expanded: Array.from(expandedGroups).join(',') }
          : {};

      const filteredCurrentParams = { ...currentQueryParams };
      if (!item.showComments) {
        delete filteredCurrentParams.showComments;
        delete filteredCurrentParams.recordId;
        delete filteredCurrentParams.recordType;
      }
      if (!item.showItems) {
        delete filteredCurrentParams.showItems;
      }

      const allQueryParams = {
        ...filteredCurrentParams,
        ...commentParams,
        ...itemsParams,
        ...expandedParam,
      };

      history.push(
        routes.myExpense.route
          .withQueryParams(allQueryParams)
          .create({ id: item.id }),
      );
    } else {
      history.push(
        routes.myExpenses.route.withQueryParams(allQueryParams).create({}),
      );
    }
  };

  const renderContent = () => {
    if (loading || !data) {
      return <Loading />;
    }

    return (
      <ExpensesTable
        onItemClick={handleOnItemClick}
        expenses={allExpenses}
        expenseGroups={allExpenseGroups}
        onDelete={handleDelete}
        onDeleteExpenseGroup={handleDeleteExpenseGroup}
        deletingExpenseGroupId={
          deleteExpenseGroupId || deleteExpenseGroupInfo?.id
        }
        submittingExpenseGroupId={submittingExpenseGroupId || undefined}
        isMyExpenses={true}
        filterStorageKey="my_expense_filter"
        createRouteUrl={(queryParams: Record<string, string>) =>
          mainRoutes.myExpenses.route.withQueryParams(queryParams).create({})
        }
        onCreateBtnClick={handleCreateBtnClick}
        onCreateExpenseGroupBtnClick={handleCreateExpenseGroupBtnClick}
        onAddExpense={handleAddExpense}
        onSubmitExpenseGroup={handleSubmitExpenseGroup}
        expandedGroups={expandedGroups}
        onExpandedGroupsChange={handleExpandedGroupsChange}
        invalidExpenseIds={invalidExpenseIds}
      />
    );
  };
  const selectedItemId = routeExpenseId ?? selectedItemWithComments?.id;
  const showComments = selectedItemWithComments?.showComments ?? false;
  const urlParams = new URLSearchParams(window.location.search);
  const showItems = urlParams.get('showItems') === 'true';
  let item = allExpenses?.find((x) => x.id === selectedItemId) ?? null;
  return (
    <>
      <PageHeader title={t('expenses.title')} />
      {renderContent()}
      {item && (
        <ExpenseSidebar
          isManager={false}
          expense={item}
          onClose={() => handleOnItemClick(null)}
          showComments={showComments}
          showItems={showItems}
        />
      )}
      <ExpenseDeleteConfirmation
        visible={!!deleteItemId}
        submitInProgress={deleteInProgress}
        onClose={handleDeleteClose}
        onSubmit={expenseDelete}
      />
      <ExpenseGroupDeleteConfirmation
        visible={!!deleteExpenseGroupInfo}
        submitInProgress={deleteExpenseGroupInProgress}
        onClose={handleDeleteExpenseGroupClose}
        onSubmit={() => expenseGroupDelete()}
        expenseGroupName={deleteExpenseGroupInfo?.name}
        childExpenseCount={deleteExpenseGroupInfo?.childCount}
      />
      <ExpenseSaveModal
        show={saveSideBarVisible}
        onSave={handleSaveSidebarClose}
        onClose={handleSaveSidebarClose}
        expenseGroupId={prefilledExpenseGroupId}
        refreshTrigger={expenseFormRefreshTrigger}
      />
      <ExpenseSaveModal
        show={editExpenseModalVisible}
        onSave={handleEditExpenseModalSave}
        onClose={handleEditExpenseModalClose}
        expenseId={editExpenseId}
        expenseGroupId={undefined}
        refreshTrigger={expenseFormRefreshTrigger}
      />
      <ExpenseGroupCreateModal
        show={createExpenseGroupModalVisible}
        onClose={handleCreateExpenseGroupModalClose}
        onSave={handleCreateExpenseGroupModalSave}
      />
    </>
  );
};

const Loading: React.FC = () => {
  return <SkeletonTable columns={5} rows={5} />;
};

export default MyExpenses;
